import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { getQueryParams, getOrigin } from '../hls/UrlUtils';
import { VgHlsSupportService } from '../hls/VgHlsSupport.Service';

import { IVbSource } from './IVbSource';
import { lastValueFrom } from './rxjs/lastValueFrom';
import { userAgentUtil } from './UserAgentUtil';

const MULTICAST_AGENT_HOST: string = 'localstreaming.vbrick.com:3443';
const REV_CONNECT_ERROR_MAXACCOUNTMESHESREACHED: string ='MaxAccountMeshesReached';
const REV_CONNECT_ERROR_MAXZONEMESHESREACHED: string = 'MaxZoneMeshesReached';
const MULTICAST_AGENT_VERSION_ALLOW_REWRITE_HEADER: string = '2.7';

@Injectable()
export class HlsUtilService {
	public readonly TYPE_VBRICK_MULTICAST: string = 'vbrick/multicast';

	constructor(
		private httpClient: HttpClient,
		private VG_HLS_SUPPORT: VgHlsSupportService
	) {}

	public get isFlashFallback(): boolean {
		return !this.VG_HLS_SUPPORT.isNative && !this.VG_HLS_SUPPORT.isMSE;
	}

	public isHLS(src: string, type: string): boolean {
		const hlsTypeRegEx: RegExp = /^application\/vnd.apple.mpegurl/i;
		const hlsAltTypeRegEx: RegExp = /^application\/x-mpegURL/i;
		const hasHlsType: boolean = hlsTypeRegEx.test(type) || hlsAltTypeRegEx.test(type);
		const hasHlsExtension: boolean = typeof src === 'string' && src.includes('.m3u8');

		return hasHlsType || hasHlsExtension;
	}

	public isMixedContent(src: string): boolean {
		return typeof src === 'string' &&
			!src.startsWith(location.protocol);
	}

	public isMulticast(src: string, type: string): boolean {
		return this.isHLS(src, type) &&
			(
				type === this.TYPE_VBRICK_MULTICAST ||
				src.includes(MULTICAST_AGENT_HOST) ||
				this.isMulticastParamsPresent(src)
			);
	}

	public isMulticastAgentRunning(src: string) {
		return lastValueFrom(this.httpClient
			.get(`${getOrigin(src)}/ping`, { responseType: 'text' }));
	}

	/**
	 * This method is used to check multicast agent version to determine whether to add rewrite header or not
	 *
	 */
	public isDisabledManifestRewriteHeader(src: string): Promise<boolean> {
		return lastValueFrom(this.httpClient
			.get(`${getOrigin(src)}/status`, { responseType: 'json' })
			.pipe(
				map((response: any) => {

					return this.isEqualMulticastAgentVersion(
						response?.buildInfo?.buildNumber,
						MULTICAST_AGENT_VERSION_ALLOW_REWRITE_HEADER
					);
				}),
				catchError((error: any) => {
					// Handle the error here
					console.error('Error occurred while fetching vbrick agent build info:', error);
					return of(false);
				})
			)
		);
	}

	public isMulticastParamsPresent(src: string): boolean {
		const params: any = getQueryParams(src);

		return params.ip &&
			params.port;
	}

	public isMulticastPlaybackSupported(src: string, type: string): boolean {
		return this.isMulticast(src, type) &&
			!userAgentUtil.isMobile &&
			!userAgentUtil.isAndroidChrome &&
			!this.isMixedContent(src) &&
			this.VG_HLS_SUPPORT.isMSE;
	}

	public isHtml5PlaybackSupported(source: IVbSource): boolean {
		const { src, type } = source;
		const isHls: boolean = this.isHLS(src, type);
		const isMulticast: boolean = this.isMulticast(src, type);

		return isHls &&
			!isMulticast &&
			!this.isMixedContent(src) &&
			!this.isUnsupportedRevConnectSource(source) &&
			!this.isFlashFallback;
	}

	public isNativePlayback(source: IVbSource): boolean {
		return userAgentUtil.isSafariIos &&
			this.isHLS(source.src, source.type);
	}

	public isUnsupportedMseMixedContentPlayback(src: string): boolean {
		return this.VG_HLS_SUPPORT.isMSE && this.isMixedContent(src);
	}

	public isRevConnect(source: IVbSource): boolean {
		return !!source.revConnectInfo;
	}

	public isRevConnectMeshFullError(error: any): boolean {
		return error &&
			(
				error.code === REV_CONNECT_ERROR_MAXACCOUNTMESHESREACHED ||
				error.code === REV_CONNECT_ERROR_MAXZONEMESHESREACHED
			);
	}

	public isUnsupportedRevConnectSource(source: IVbSource): boolean {
		const isBrowserLackingRtcDataChannelSupport: boolean = userAgentUtil.isIeOrLegacyEdge || !window.RTCPeerConnection; // legacy Edge lacks Data Channel support

		return this.isRevConnect(source) &&
			(
				isBrowserLackingRtcDataChannelSupport ||
				userAgentUtil.isAndroidChrome ||
				userAgentUtil.isSafariIos
			);
	}

	private isEqualMulticastAgentVersion(currentVersion: string, targetVersion: string): boolean {
		if (!currentVersion || !targetVersion) {
			return;
		}
		const buildNumberParts = currentVersion.split('.');
		const majorVersion = parseInt(buildNumberParts[0], 10);
		const minorVersion = parseInt(buildNumberParts[1], 10);

		const targetBuildNumberParts = targetVersion.split('.');
		const targetMajorVersion = parseInt(targetBuildNumberParts[0], 10);
		const targetMinorVersion = parseInt(targetBuildNumberParts[1], 10);

		return majorVersion === targetMajorVersion && minorVersion === targetMinorVersion;
	}
}
